import type { PaymentDirection } from '@cpce/database/enums';
import type {
  ConnectableClientAccount,
  ConnectableSupplierAccount,
  FinancialAccountOption,
  FinancialAccountsFilters,
  FinancialAccountWithRelations,
  MissingClientAccountsCount,
  PaymentMethodOption,
} from '@cpce/shared';
import { isPostableAccountCode } from '@cpce/shared';
import { useMemo } from 'react';
import { commiteeQuery } from '@/api/queries.utils';
import { financialAccountsQueryKeys } from './financial-accounts.query-keys';

export type BankAccountOption = PaymentMethodOption;

export function useFinancialAccountsCount() {
  return commiteeQuery<{ count: number }>({
    queryKey: financialAccountsQueryKeys.count(),
    url: '/commitee/:commiteeId/financial-accounts/count',
  });
}

export function useFinancialAccountOptions() {
  return commiteeQuery<FinancialAccountOption[]>({
    queryKey: financialAccountsQueryKeys.options(),
    url: '/commitee/:commiteeId/financial-accounts/options',
  });
}

// Modes de règlement usable for one direction: inflow modes for a règlement received (salarié),
// outflow modes for a payment issued (fournisseur). Despite the endpoint's name these are the
// flagged treasury accounts, not the CSE's banks.
export function usePaymentMethodAccounts(direction: PaymentDirection) {
  return commiteeQuery<BankAccountOption[]>({
    queryKey: financialAccountsQueryKeys.bankAccounts(direction),
    url: '/commitee/:commiteeId/financial-accounts/bank-accounts',
    params: { direction },
  });
}

export function useFinancialAccounts(filters: FinancialAccountsFilters = {}) {
  // Axios silently drops `null` query params, so explicit "no parent" (root-only) filters would
  // disappear on the wire. Send the literal string `'null'` instead — the backend zod schema
  // preprocesses it back to `null`.
  const wireFilters = {
    ...filters,
    parentFinancialAccountId:
      filters.parentFinancialAccountId === null ? 'null' : filters.parentFinancialAccountId,
  };
  return commiteeQuery<FinancialAccountWithRelations[]>({
    queryKey: financialAccountsQueryKeys.filtered(filters),
    url: '/commitee/:commiteeId/financial-accounts',
    params: wireFilters,
  });
}

export function useFinancialAccount(id: number, enabled = true) {
  return commiteeQuery<FinancialAccountWithRelations>({
    queryKey: financialAccountsQueryKeys.one(id),
    url: `/commitee/:commiteeId/financial-accounts/${id}`,
    enabled,
  });
}

export function useNextSubAccountCode(parentId: number | null) {
  return commiteeQuery<{ code: string }>({
    queryKey: financialAccountsQueryKeys.nextCode(parentId ?? 0),
    url: `/commitee/:commiteeId/financial-accounts/next-code/${parentId}`,
    enabled: parentId !== null,
  });
}

export function useConnectableClientAccounts(enabled = true) {
  return commiteeQuery<ConnectableClientAccount[]>({
    queryKey: financialAccountsQueryKeys.connectableClientAccounts(),
    url: '/commitee/:commiteeId/financial-accounts/connectable-client-accounts',
    enabled,
  });
}

export function useClientAccountNextCode(enabled = true) {
  return commiteeQuery<{ code: string }>({
    queryKey: financialAccountsQueryKeys.clientAccountNextCode(),
    url: '/commitee/:commiteeId/financial-accounts/client-accounts/next-code',
    enabled,
  });
}

export function useUnusedClientAccountsCount(enabled = true) {
  return commiteeQuery<{ count: number }>({
    queryKey: financialAccountsQueryKeys.unusedClientAccountsCount(),
    url: '/commitee/:commiteeId/financial-accounts/client-accounts/unused-count',
    enabled,
  });
}

export function useMissingClientAccountsCount(enabled = true) {
  return commiteeQuery<MissingClientAccountsCount>({
    queryKey: financialAccountsQueryKeys.missingClientAccountsCount(),
    url: '/commitee/:commiteeId/financial-accounts/client-accounts/missing-count',
    enabled,
  });
}

export function useConnectableSupplierAccounts(enabled = true) {
  return commiteeQuery<ConnectableSupplierAccount[]>({
    queryKey: financialAccountsQueryKeys.connectableSupplierAccounts(),
    url: '/commitee/:commiteeId/financial-accounts/connectable-supplier-accounts',
    enabled,
  });
}

export function useSupplierAccountNextCode(enabled = true) {
  return commiteeQuery<{ code: string }>({
    queryKey: financialAccountsQueryKeys.supplierAccountNextCode(),
    url: '/commitee/:commiteeId/financial-accounts/supplier-accounts/next-code',
    enabled,
  });
}

// For pickers whose account receives écritures: classes and sub-classes only group accounts.
// Showing only code and name, they share the options list rather than the plan comptable's full
// payload (relations and balances of every compte: megabytes on a CSE with thousands of 411).
export function usePostableFinancialAccountOptions(codeStartsWith = '') {
  const query = useFinancialAccountOptions();
  const data = useMemo(
    () =>
      query.data?.filter(
        (account) => account.code.startsWith(codeStartsWith) && isPostableAccountCode(account.code)
      ),
    [query.data, codeStartsWith]
  );
  return { ...query, data };
}

// Leaves only, for a default or line account: a parent would post on an aggregate.
export function useLeafAccountOptions(codeStartsWith: string) {
  const query = usePostableFinancialAccountOptions(codeStartsWith);
  const data = useMemo(() => query.data?.filter((account) => account.isLeaf), [query.data]);
  return { ...query, data };
}

export function usePostableFinancialAccounts(filters: FinancialAccountsFilters = {}) {
  const query = useFinancialAccounts(filters);
  const data = useMemo(
    () => query.data?.filter((account) => isPostableAccountCode(account.code)),
    [query.data]
  );
  return { ...query, data };
}
